import { z } from 'zod';
/**
 * AgentMiss + AgentCalibrationSummary — the "things I got wrong" surface
 * (issue #166 PR A — calibration page). Each miss is one decision the
 * agent later learned was wrong, stamped with what the agent said, what
 * was true, the lesson banked in memory (the ◆ Learned ... callout), and
 * a categorical weight used to roll the "weak areas" view on the
 * calibration page.
 *
 * Persisted at `organizations/{orgId}/agentMisses/{id}`; surfaced by
 * `GET /api/orgs/:o/agent/calibration` which returns both an array of
 * the recent misses AND a roll-up summary (decisions / accuracy /
 * quarter-over-quarter delta / corrections + learnings counts / weak
 * areas).
 */
/** Categorical weight — drives the "weak areas" roll-up on the calibration page. */
export const AgentMissWeight = z.enum(['high', 'med', 'low']);
export const AgentMiss = z.object({
    id: z.string(),
    orgId: z.string(),
    /** When the miss happened. */
    date: z.string().datetime(),
    /** What the agent said at the time. */
    what: z.string(),
    /** What turned out to be true. */
    actual: z.string(),
    /** The ◆ Learned ... callout — the lesson banked in agent memory. */
    lesson: z.string(),
    /** Severity bucket — feeds the calibration page's weak-areas roll-up. */
    weight: AgentMissWeight,
});
/**
 * AgentCalibrationSummary — aggregate shape returned by
 * `GET /api/orgs/:o/agent/calibration`. The portal's calibration page
 * reads this directly into its hero counters + weak-areas list.
 *
 *   decisions:        total decisions reviewed this quarter
 *   accuracy:         0..1 — calibrated accuracy across those decisions
 *   deltaQuarter:     +/- absolute change from prior quarter
 *   correctionsCount: feedback events of kind 'correction-ack'
 *   learningsCount:   how many lessons banked in memory this quarter
 *   weakAreas:        per-topic accuracy roll-up for the weak-areas list
 */
export const AgentCalibrationSummary = z.object({
    decisions: z.number().int().nonnegative(),
    accuracy: z.number().min(0).max(1),
    deltaQuarter: z.number(),
    correctionsCount: z.number().int().nonnegative(),
    learningsCount: z.number().int().nonnegative(),
    weakAreas: z.array(z.object({
        category: z.string(),
        accuracy: z.number().min(0).max(1),
    })),
});
